import axios from 'axios';
import { getApiUrl } from '../../../config/api';

/**
 * API client for auto-sourcing — recommended candidates for a JD.
 * Mirrors the other employer clients: company identity comes from the session,
 * with a company_email fallback from localStorage for cross-origin dev.
 */

const ACTOR_KEYS = [
  'admin_email', 'employer_email', 'recruiter_email', 'company_email', 'organizer_email',
];

const readStored = (key) => {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key) || '';
};

const getCompanyEmail = () => {
  if (typeof window === 'undefined') return '';
  for (const key of ACTOR_KEYS) {
    const v = readStored(key);
    if (v) return v.trim().toLowerCase();
  }
  return '';
};

/**
 * Fetch recommended candidates for a job. Server caches per-JD for a day;
 * pass { refresh: true } to recompute.
 *
 * { external: true } additionally asks the server to search the PAID external
 * profile source for this job. Only send it on an explicit recruiter action:
 * it can spend credits. The server decides whether it actually runs (signed-in
 * identity, monthly limit, unchanged search) and reports the outcome in
 * `external.status` on the response.
 */
export async function getRecommendedCandidates(jobId, { refresh = false, external = false } = {}) {
  const params = {};
  const company = getCompanyEmail();
  if (company) params.company_email = company;
  if (refresh) params.refresh = 1;
  if (external) params.external = 1;
  const res = await axios.get(
    getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/recommended-candidates`),
    { params, withCredentials: true }
  );
  return res.data;
}

/** Save a recommended candidate as a sourced lead for this job. */
export async function saveSourcedCandidate(jobId, candidate) {
  const company = getCompanyEmail();
  const res = await axios.post(
    getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/sourced-candidates`),
    { company_email: company || undefined, candidate },
    { withCredentials: true }
  );
  return Boolean(res.data?.saved);
}

/**
 * Unlock a shared talent-pool candidate for this job. Saving IS the unlock:
 * the server re-checks that the candidate is still visible to this company,
 * records the reveal, saves the lead and returns the contact details
 * ({ saved, unlocked, candidate: { dedupe_key, name, email, resume_url } }).
 * The card is identified by its opaque `tp:<id>` key — the browser never holds
 * the candidate's email before this call succeeds.
 */
export async function unlockTalentPoolCandidate(jobId, candidate) {
  const company = getCompanyEmail();
  const res = await axios.post(
    getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/sourced-candidates`),
    {
      company_email: company || undefined,
      candidate: {
        dedupe_key: candidate.dedupe_key,
        headline: candidate.headline,
        location: candidate.location,
        skills: candidate.skills,
        fit_score: candidate.fit_score,
        fit_reason: candidate.fit_reason,
      },
    },
    { withCredentials: true }
  );
  return res.data || {};
}

/**
 * Save this job's "must include" keywords (an array of strings; alternatives
 * inside one keyword are written "first / second"). An empty array clears
 * them. The server validates and returns the saved labels; call
 * getRecommendedCandidates(jobId, { refresh: true }) afterwards.
 */
export async function saveMustKeywords(jobId, keywords) {
  const company = getCompanyEmail();
  const res = await axios.put(
    getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/recommended-keywords`),
    { company_email: company || undefined, keywords },
    { withCredentials: true }
  );
  return Array.isArray(res.data?.keywords) ? res.data.keywords : [];
}

/** Record that a recruiter opened a candidate's profile (drives the "Viewed"
 *  tag). Non-fatal — viewing should never block on this. */
export async function markCandidateViewed(jobId, dedupeKey) {
  const company = getCompanyEmail();
  try {
    await axios.post(
      getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/viewed`),
      { company_email: company || undefined, dedupe_key: dedupeKey },
      { withCredentials: true }
    );
  } catch (e) {
    /* non-fatal */
  }
  return true;
}

/** List the saved sourced leads for a job. */
export async function listSourcedCandidates(jobId) {
  const params = {};
  const company = getCompanyEmail();
  if (company) params.company_email = company;
  const res = await axios.get(
    getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/sourced-candidates`),
    { params, withCredentials: true }
  );
  return Array.isArray(res.data?.candidates) ? res.data.candidates : [];
}

/** Remove a saved sourced lead from this job. */
export async function removeSourcedCandidate(jobId, dedupeKey) {
  const company = getCompanyEmail();
  const res = await axios.delete(
    getApiUrl(`/api/jobs/${encodeURIComponent(jobId)}/sourced-candidates`),
    {
      data: { company_email: company || undefined, dedupe_key: dedupeKey },
      params: company ? { company_email: company } : undefined,
      withCredentials: true,
    }
  );
  return Boolean(res.data?.removed);
}

